import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getRecords } from '../services/api';

export default function HomePage() {
  const { user, isSuperAdmin, isAdminOrAbove } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getRecords()
      .then(res => setRecords(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const counts = {
    total:       records.length,
    fixed:       records.filter(r => r.status === 'fixed').length,
    needs_parts: records.filter(r => r.status === 'needs_parts').length,
    unusable:    records.filter(r => r.status === 'unusable').length,
  };

  const statCards = [
    { label: 'සම්පූර්ණ වාර්තා',  value: counts.total,       bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200',   icon: '📋' },
    { label: 'යථා තත්ත්වය',       value: counts.fixed,       bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200',  icon: '✅' },
    { label: 'කොටස් අවශ්‍ය',     value: counts.needs_parts, bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', icon: '🔧' },
    { label: 'භාවිත කළ නොහැක',   value: counts.unusable,    bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',    icon: '❌' },
  ];

  return (
    <div>
      {/* Welcome */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          සාදරයෙන් පිළිගනිමු, {user?.name} 👋
        </h1>
        <p className="text-gray-500 mt-1 text-sm">
          දෘඩාංග නඩත්තු ව්‍යාපෘතිය — සාරාංශය
        </p>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="text-center py-10 text-gray-400">පූරණය වෙමින්...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {statCards.map(({ label, value, bg, text, border, icon }) => (
              <div key={label} className={`${bg} ${border} border rounded-xl p-5`}>
                <div className="text-3xl mb-2">{icon}</div>
                <div className={`text-3xl font-bold ${text}`}>{value}</div>
                <div className={`text-sm mt-1 font-medium ${text}`}>{label}</div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <h2 className="text-base font-semibold text-gray-700 mb-4">ඉක්මන් ක්‍රියා</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">

              {/* View Records — all roles */}
              <Link to="/maintenance"
                className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-5 hover:border-red-300 hover:shadow-sm transition group">
                <div className="bg-red-50 rounded-lg p-3 group-hover:bg-red-100 transition">
                  <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">වාර්තා බලන්න</p>
                  <p className="text-xs text-gray-500 mt-0.5">සියලු නඩත්තු වාර්තා</p>
                </div>
              </Link>

              {/* Add Record — admin and above */}
              {isAdminOrAbove && (
                <Link to="/maintenance/new"
                  className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-5 hover:border-red-300 hover:shadow-sm transition group">
                  <div className="bg-red-50 rounded-lg p-3 group-hover:bg-red-100 transition">
                    <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">නව වාර්තාව</p>
                    <p className="text-xs text-gray-500 mt-0.5">නව නඩත්තු වාර්තාව</p>
                  </div>
                </Link>
              )}

              {/* Manage Schools — super admin only */}
              {isSuperAdmin && (
                <Link to="/schools"
                  className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-5 hover:border-red-300 hover:shadow-sm transition group">
                  <div className="bg-red-50 rounded-lg p-3 group-hover:bg-red-100 transition">
                    <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">පාසල් කළමනාකරණය</p>
                    <p className="text-xs text-gray-500 mt-0.5">පාසල් එකතු / සංස්කරණය</p>
                  </div>
                </Link>
              )}

              {/* Manage Employees — super admin only */}
              {isSuperAdmin && (
                <Link to="/employees"
                  className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-5 hover:border-red-300 hover:shadow-sm transition group">
                  <div className="bg-red-50 rounded-lg p-3 group-hover:bg-red-100 transition">
                    <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">නිලධාරීන්</p>
                    <p className="text-xs text-gray-500 mt-0.5">නිලධාරීන් කළමනාකරණය</p>
                  </div>
                </Link>
              )}

              {/* Manage Users — super admin only */}
              {isSuperAdmin && (
                <Link to="/users"
                  className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-5 hover:border-red-300 hover:shadow-sm transition group">
                  <div className="bg-red-50 rounded-lg p-3 group-hover:bg-red-100 transition">
                    <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">පරිශීලකයන්</p>
                    <p className="text-xs text-gray-500 mt-0.5">Users කළමනාකරණය</p>
                  </div>
                </Link>
              )}

            </div>
          </div>

          {/* Recent Records */}
          {records.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-700">මෑත වාර්තා</h2>
                <Link to="/maintenance" className="text-sm text-red-500 hover:underline">
                  සියල්ල බලන්න →
                </Link>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        {['පාසල', 'වර්ගය', 'උපකරණය', 'දිනය', 'තත්ත්වය'].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {records.slice(0, 5).map(r => (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{r.school_number}</td>
                          <td className="px-4 py-3 text-gray-600 capitalize">{r.type?.replace('_', ' ') || '-'}</td>
                          <td className="px-4 py-3 text-gray-600">{r.device_name || '-'}</td>
                          <td className="px-4 py-3 text-gray-500">{r.opened_date?.slice(0, 10) || '-'}</td>
                          <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    fixed:       'bg-green-100 text-green-700',
    needs_parts: 'bg-yellow-100 text-yellow-700',
    unusable:    'bg-red-100 text-red-700',
  };
  const labels = {
    fixed:       'යථා තත්ත්වය',
    needs_parts: 'කොටස් අවශ්‍ය',
    unusable:    'භාවිත නොහැක',
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-500'}`}>
      {labels[status] || status || '-'}
    </span>
  );
}